/**
 * Dữ liệu mẫu nhật ký thao tác (`audit_log`).
 *
 * Sinh theo mốc **lúc nạp trang** để bộ lọc "Hôm nay", "24 giờ qua" luôn có
 * dòng. Có đủ nhánh xấu: DENY của Gateway, bản ghi đã che của tenant khác
 * (ADR-0010), job hệ thống không có `user_id`, `detail` rỗng.
 */
import type { NguonGhi, QuyetDinh, SuKienAudit } from '../modules/nhat-ky/types';

type Mau = [
  action: string,
  resource: string | null,
  decision: QuyetDinh,
  source: NguonGhi,
  detail: Record<string, unknown> | null,
];

const MAU: Mau[] = [
  ['auth.login_error', null, 'DENY', 'KEYCLOAK', { error: 'invalid_user_credentials', client_id: 'web-admin' }],
  ['USER_PROFILE_UPDATED', '/api/v1/users/u-016', 'ALLOW', 'SERVICE', {
    changes: [{ field: 'display_name', before: 'Hoàng Anh', after: 'Hoàng Đức Anh' }],
  }],
  ['ip.monitor', '/api/v1/m/ocr/jobs', 'ALLOW', 'GATEWAY', { would_deny: 'IP_NOT_ALLOWED', mode: 'MONITOR' }],
  ['security.policy.updated', '/api/v1/tenants/security', 'ALLOW', 'SERVICE', {
    changes: [{ key: 'session.idle_timeout_minutes', before: 30, after: 15 }],
  }],
  ['POST', '/api/v1/m/ocr/jobs', 'DENY', 'GATEWAY', { error_code: 'PERMISSION_DENIED' }],
  ['GET', '/api/v1/m/van-ban/documents', 'ALLOW', 'SDK', { sampled: true }],
  ['USER_INVITED', '/api/v1/users/invite', 'ALLOW', 'SERVICE', null],
  ['GET', '/api/v1/tenants/0a7d3c5e-1f2b-4e6a-9c8d-000000000002/users', 'DENY', 'GATEWAY', { error_code: 'TENANT_MISMATCH' }],
  ['ROLE_PERMISSIONS_CHANGED', '/api/v1/roles/ke-toan-truong/permissions', 'ALLOW', 'SERVICE', {
    changes: [{ field: 'permissions', before: ['DOC_MGMT.READ'], after: ['DOC_MGMT.READ', 'DOC_MGMT.UPDATE'] }],
  }],
  ['auth.user_temporarily_locked', null, 'DENY', 'KEYCLOAK', { failures: 5 }],
  ['api_key.created', '/api/v1/api-keys', 'ALLOW', 'SERVICE', { scopes: ['ocr:job:create'] }],
  ['audit.export', '/api/v1/tenants/0a7d3c5e-1f2b-4e6a-9c8d-000000000002/audit/export', 'ALLOW', 'SERVICE', { result_count: 4210 }],
  ['llm.goi', '/api/v1/m/ocr/jobs/42', 'ALLOW', 'SDK', { model: 'foxai-ocr-1', tokens: 1834 }],
  ['MODULE_INSTALLED', '/api/v1/tenants/modules/van-ban', 'ALLOW', 'SERVICE', { version: '1.4.0' }],
  // Tên hàm của bản ghi DENY tự động: mã ngoài danh mục, hiện "chưa có trong danh mục".
  ['getTenantSecurity', '/api/v1/tenants/security', 'DENY', 'SERVICE', { error_code: 'PERMISSION_DENIED' }],
];

/**
 * Người thực hiện: người dùng của tổ chức mẫu (`mock/nguoi-dung.ts`) và một
 * nhân sự nền tảng (`mock/nhan-su-nen-tang.ts`), nên ô lọc người dùng chọn
 * được đúng người có dòng.
 */
const NGUOI = ['u-004', 'u-010', 'u-016', 'u-018', 'ns-002', null] as const;
/** Tên audit-service tra từ danh bạ của tenant; `ns-002` là nhân sự nền tảng, không có trong danh bạ. */
const TEN_DANH_BA: Record<string, string> = {
  'u-004': 'Phạm Thu Hà',
  'u-010': 'Đỗ Hoài Nam',
  'u-016': 'Hoàng Đức Anh',
  'u-018': 'Phan Thanh Tùng',
};
const IP = ['113.161.42.18', '14.232.10.4', '171.244.18.90', '2402:800:6371:1a2b::17', null] as const;

function sinh(tenantId: string, soDong: number, buocPhut: number): SuKienAudit[] {
  const goc = Date.now() - 4 * 60_000;
  return Array.from({ length: soDong }, (_, i) => {
    const [action, resource, decision, source, detail] = MAU[i % MAU.length]!;
    const che = detail?.error_code === 'TENANT_MISMATCH';
    const so = String(i + 1).padStart(4, '0');
    return {
      id: `${tenantId}-a${so}`,
      event_id: `9f1c${so}-7b2e-4c11-9a0d-${tenantId.replace('-', '')}00${so}`,
      tenant_id: tenantId,
      // Bản ghi do user tenant khác gây ra: audit-service trả dạng đã che.
      user_id: che ? 'u-•••' : action === 'audit.export' ? null : NGUOI[i % 5]!,
      user_name: che || action === 'audit.export' ? null : (TEN_DANH_BA[NGUOI[i % 5]!] ?? null),
      action,
      resource,
      decision,
      ip: che ? '•••' : IP[i % IP.length]!,
      source,
      detail,
      created_at: new Date(goc - i * buocPhut * 60_000).toISOString(),
    };
  });
}

/** 180 dòng cho `0a7d3c5e-1f2b-4e6a-9c8d-000000000002` (khoảng 45 ngày), ít dòng hơn cho tenant khác. */
export const MOCK_AUDIT: SuKienAudit[] = [
  ...sinh('0a7d3c5e-1f2b-4e6a-9c8d-000000000002', 180, 360),
  ...sinh('0a7d3c5e-1f2b-4e6a-9c8d-000000000001', 60, 180),
  ...sinh('0a7d3c5e-1f2b-4e6a-9c8d-000000000005', 12, 600),
];
